import { Params } from '@angular/router';

import { Duration } from 'dayjs/plugin/duration';

import { isString, SDate, SDuration } from '@summit/core';

// Needs to align with TableDisplay formatting for Excel

export function RateFormatter(rate: number): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3
  }).format(rate);
}

export function MoneyFormatter(rate: number): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD'
  }).format(rate);
}

export function FinancialFormatter(rate: number): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(rate);
}

export function OdometerFormatter(mileage: number): string {
  if (mileage == null || mileage == undefined) return '--';
  return new Intl.NumberFormat('en', {
    useGrouping: true,
    maximumFractionDigits: 1
  }).format(mileage);
}

export function MileageFormatter(mileage: number): string {
  if (mileage == null || mileage == undefined) return '--';
  return new Intl.NumberFormat('en', {
    useGrouping: true,
    maximumFractionDigits: 0
  }).format(mileage);
}

export function NumberSortString(rate: number, minimumDigits?: number): string {
  return new Intl.NumberFormat('en', {
    useGrouping: false,
    minimumIntegerDigits: minimumDigits ? minimumDigits : 1
  }).format(rate);
}

export function NumberFormatter(rate: number, minimumDigits?: number): string {
  return new Intl.NumberFormat('en', {
    useGrouping: true,
    minimumIntegerDigits: minimumDigits ? minimumDigits : 1
  }).format(rate);
}

export function FuelFormatter(rate: number): string {
  return new Intl.NumberFormat('en', {
    useGrouping: true,
    minimumFractionDigits: 2
  }).format(rate);
}

export function Number2PlacesFormatter(rate: number, places?: number): string {
  places ??= 2;
  return new Intl.NumberFormat('en', {
    useGrouping: true,
    minimumFractionDigits: places,
    maximumFractionDigits: places
  }).format(rate);
}

export function PercentageFormatter(rate: number, precision = 1): string {
  return new Intl.NumberFormat('en', {
    useGrouping: true,
    style: 'percent',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  }).format(rate);
}
export function DurationFormatter(input: Duration): string | undefined {
  if (SDuration.isDuration(input)) {
    const minutes = Math.floor(input.minutes());
    return `${NumberFormatter(Math.trunc(input.asHours()), 0)}:${NumberFormatter(minutes, 2)}`;
  }
  if (!input) return undefined;
  return 'Error';
}
export function HosDurationFormatter(input: Duration): string | undefined {
  if (SDuration.isDuration(input)) {
    const hours = Math.trunc(input.asHours());
    const minutes = Math.floor(input.minutes());
    return hours > 0
      ? `${NumberFormatter(hours, 0)}h ${NumberFormatter(minutes, 2)} m`
      : `${NumberFormatter(minutes, 2)} m`;
  }
  if (!input) return undefined;
  return 'Error';
}
export function TimeFormatter(input: Duration, timeFormat: 12 | 24): string | undefined {
  if (timeFormat == 12) {
    let hourPeriod = 'AM';
    let hours = SDuration.isDuration(input) ? input.hours() : 0;
    if (hours >= 12) {
      hourPeriod = 'PM';
      if (hours > 12) hours -= 12;
    } else {
      if (hours == 0) {
        hours = 12;
      }
    }
    const pickerString = `${NumberFormatter(hours, 2)}:${NumberFormatter(input.minutes(), 2)} ${hourPeriod}`;
    //console.log(`picker string = ${pickerString}`);
    return pickerString;
  }
  return DurationFormatter(input);
}

export function ConvertToUrlParameters<T extends object>(object: T): Params {
  const urlParameters = new Object() as Params;
  Object.entries(object).forEach(([key, value]) => {
    if (isString(value)) {
      urlParameters[key] = value;
    } else if (SDate.isDayjs(value)) {
      urlParameters[key] = value.toISOString();
    } else {
      urlParameters[key] = JSON.stringify(value);
    }
  });
  return urlParameters;
}

export function ToCamelCase(input: string): string {
  if (input === input.toUpperCase()) {
    return input.toLowerCase();
  }
  return input[0].toLowerCase() + input.substring(1);
}

export function ToCCase(input: string): string {
  return input[0].toUpperCase() + input.substring(1);
}

export function ToProperNameCase(input: string, abbreviationLength?: number): string | undefined {
  if (input == null || input == undefined || input.length === 0) return undefined;
  return input
    .split(' ')
    .map((word) =>
      word?.length > 0
        ? word.length > (abbreviationLength ?? 0)
          ? word[0].toUpperCase() + word.substring(1).toLowerCase()
          : word
        : ''
    )
    .join(' ');
}

export function HtmlToText(html: string): string {
  //Sync with server
  let lclHtml = html.replace(/<style([\s\S]*?)<\/style>/gi, '');
  lclHtml = lclHtml.replace(/<script([\s\S]*?)<\/script>/gi, '');
  lclHtml = lclHtml.replace(/<\/div>/gi, '\n');
  lclHtml = lclHtml.replace(/<\/li>/gi, '\n');
  lclHtml = lclHtml.replace(/<li>/gi, '  *  ');
  lclHtml = lclHtml.replace(/<\/ul>/gi, '\n');
  lclHtml = lclHtml.replace(/<\/p>/gi, '\n');
  lclHtml = lclHtml.replace(/<br\s*[/]?>/gi, '\n');
  lclHtml = lclHtml.replace(/<[^>]+>/gi, '');
  lclHtml = lclHtml.replace(/&gt;/gi, '>');
  lclHtml = lclHtml.replace(/&lt;/gi, '<');
  lclHtml = lclHtml.replace(/&nbsp;/gi, ' ');
  lclHtml = lclHtml.replace(/&amp;/gi, '&');
  lclHtml = lclHtml.replace(/&quot;/gi, '"');
  lclHtml = lclHtml.replace(/&apos;/gi, "'");
  lclHtml = lclHtml.replace(/&#39;/gi, '`');
  return lclHtml;
}

export function PadLeft(input: string, length: number, padChar: string = ' '): string {
  if (input.length >= length) return input;
  return padChar.repeat(length - input.length) + input;
}

export function PadRight(input: string, length: number, padChar: string = ' '): string {
  if (input.length >= length) return input;
  return input + padChar.repeat(length - input.length);
}

export function PadAround(input: string, length: number, padChar: string = ' '): string {
  if (input.length >= length) return input;
  const totalPadding = length - input.length;
  const padLeft = Math.floor(totalPadding / 2);
  const padRight = totalPadding - padLeft;
  return padChar.repeat(padLeft) + input + padChar.repeat(padRight);
}
